import Rating from 'components/ui/Rating/Rating';
import { FC, Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { i18nTB } from 'utils/shared/utils';
import {
  TripPreviewFlightCard,
  TripPreviewHotelCard,
  TripPreviewHotelRoomInfo,
  TripPreviewModel,
  TripPreviewSlotView,
  TripPreviewTransferCard,
} from 'utils/shared/tripResultBuilderPreview';
import { ProposalData } from 'ts/shared/interfaces/ProposalData';
import TripBuilderGetPdfRoute from './TripBuilderGetPdfRoute';
import { getBlobUrl, resolveGalleryImageUrl } from 'utils/shared/tripBuilderImages';
const getCompoundFlightBackground = (color: string) => `color-mix(in srgb, ${color} 20%, white)`;

const parsePriceLabelParts = (label: string | null | undefined) => {
  if (!label) {
    return null;
  }

  const cleaned = label.replace(/^From\s+/i, '').trim();
  const amountMatch = cleaned.match(/([\d][\d,.]*)/);
  if (!amountMatch) {
    return { currency: '', amount: cleaned };
  }

  return {
    currency: cleaned.replace(amountMatch[0], '').trim(),
    amount: amountMatch[1],
  };
};

const PdfPrice: FC<{ label?: string | null; className?: string }> = ({ label, className }) => {
  const parts = parsePriceLabelParts(label);
  if (!parts?.amount) {
    return null;
  }

  return (
    <div className={'tbp-price-amount' + (className ? ` ${className}` : '')}>
      {!!parts.currency && <span className="tbp-price-currency">{parts.currency}</span>}
      <span className="tbp-price-value">{parts.amount}</span>
    </div>
  );
};

type TripBuilderGetPdfContentProps = {
  model: TripPreviewModel;
  proposal: ProposalData;
  selectedRoomByHotel: Record<string, number>;
  hidePrices?: boolean;
  hotelImageBySlot?: Record<string, string>;
  uiVersion?: 1 | 2;
};

const PdfFlightCard: FC<{ card: TripPreviewFlightCard; hidePrices?: boolean }> = ({
  card,
  hidePrices,
}) => {
  const { t } = useTranslation(undefined, { i18n: i18nTB });
  const [fromCode, toCode] = card.route.split('->').map((item) => item.trim());
  const departureTime =
    card.departureTime || (card.scheduleLabel || '').split('-')[0]?.trim().split(' ').pop() || '-';
  const arrivalTime =
    card.arrivalTime || (card.scheduleLabel || '').split('-')[1]?.trim().split(' ')[0] || '-';
  const showPrice = !hidePrices && !!card.priceLabel;

  return (
    <div
      className={'tbp-flight-card' + (showPrice ? '' : ' tbp-flight-card-no-price')}
      style={
        card.compoundColor ? { backgroundColor: getCompoundFlightBackground(card.compoundColor) } : undefined
      }
    >
      <div className="tbp-flight-left">
        <div className="tbp-flight-main">
          <div className="tbp-flight-row-labels">
            <span>{t('departure', { label: card.departureLabel || '-' })}</span>
            <span>{t('arrival', { label: card.arrivalLabel || '-' })}</span>
          </div>
          <div className="tbp-flight-row-codes">
            <span>{fromCode || '-'}</span>
            <span className="tbp-flight-arrow">
              <span className="tbp-flight-arrow-icon" aria-hidden="true">
                ✈
              </span>
              <span className="tbp-flight-arrow-line" aria-hidden="true" />
              {!!card.durationLabel && (
                <span className="tbp-flight-duration">{card.durationLabel}</span>
              )}
            </span>
            <span>{toCode || '-'}</span>
          </div>
          <div className="tbp-flight-row-times">
            <span>{departureTime}</span>
            <span>{arrivalTime}</span>
          </div>
          {(!!card.flightInfoLabel || !!card.arrivalDetailLabel) && (
            <div className="tbp-flight-row-meta">
              {!!card.flightInfoLabel && <span>{card.flightInfoLabel}</span>}
              {!!card.arrivalDetailLabel && <span>{card.arrivalDetailLabel}</span>}
            </div>
          )}
        </div>
      </div>
      {showPrice && (
        <div className="tbp-flight-right">
          <PdfPrice label={card.priceLabel} className="tbp-flight-price" />
        </div>
      )}
    </div>
  );
};

const PdfRoomTags: FC<{ room: TripPreviewHotelRoomInfo }> = ({ room }) => (
  <div className="tbp-option-room-row">
    {!!room.roomLabel && (
      <span className="tbp-option-tag tbp-option-tag-room">{room.roomLabel}</span>
    )}
    {!!room.boardLabel && (
      <span className="tbp-option-tag tbp-option-tag-board">{room.boardLabel}</span>
    )}
    {!!room.cxlLabel && room.cxlLabel === 'Full cancellation fee' && (
      <span className="tbp-option-tag tbp-option-tag-cxl tbp-option-tag-cxl-danger">
        {room.cxlLabel}
      </span>
    )}
    {!!room.cxlLabel && room.cxlLabel.includes('Free cancel until') && (
      <span className="tbp-option-tag tbp-option-tag-cxl tbp-option-tag-cxl-success">
        {room.cxlLabel}
      </span>
    )}
    {!!room.cxlLabel &&
      room.cxlLabel !== 'Full cancellation fee' &&
      !room.cxlLabel.includes('Free cancel until') && (
        <span className="tbp-option-tag tbp-option-tag-cxl">{room.cxlLabel}</span>
      )}
  </div>
);

const PdfHotelImage: FC<{ src?: string }> = ({ src }) => {
  const [failed, setFailed] = useState(false);

  if (!src || failed) {
    return <div className="tbp-option-card-img tbp-option-card-img-empty" aria-hidden />;
  }

  return (
    <img
      className="tbp-option-card-img"
      src={src}
      alt=""
      onError={() => setFailed(true)}
    />
  );
};

const PdfHotelCard: FC<{
  card: TripPreviewHotelCard;
  selectedRoomIndex: number;
  hidePrices?: boolean;
}> = ({ card, selectedRoomIndex, hidePrices }) => {
  const { t } = useTranslation(undefined, { i18n: i18nTB });
  const selectedRoom = (card.rooms || [])[selectedRoomIndex];
  const displayPrice =
    selectedRoom?.priceLabel ?? (card.priceLabel ? card.priceLabel.replace(/^From\s+/i, '') : null);

  return (
    <div className="tbp-option-card">
      <PdfHotelImage src={card.logoCode} />
      <div className="tbp-option-card-left">
        <div className="tbp-option-card-name">{card.name}</div>
        {!!card.star && <Rating value={card.star} className="tbp-option-rating" />}
        {selectedRoom && (selectedRoom.roomLabel || selectedRoom.boardLabel || selectedRoom.cxlLabel) && (
          <div className="tbp-option-tags">
            <PdfRoomTags room={selectedRoom} />
          </div>
        )}
      </div>
      <div className="tbp-option-card-right">
        {!hidePrices && !!displayPrice && <PdfPrice label={displayPrice} className="tbp-option-price" />}
        {!hidePrices && !!displayPrice && <div className="tbp-option-price-sub">{t('total_stay')}</div>}
      </div>
    </div>
  );
};

const PdfTransferCard: FC<{ card: TripPreviewTransferCard; hidePrices?: boolean }> = ({
  card,
  hidePrices,
}) => {
  const routeLabel = [card.pickUpLabel, card.dropOffLabel].filter(Boolean).join(' → ');

  return (
    <div className="tbp-option-card">
      <div className="tbp-option-card-left">
        <div className="tbp-option-card-name">{card.name}</div>
        {!!routeLabel && <div className="tbp-option-meta">{routeLabel}</div>}
        {!!card.scheduleLabel && <div className="tbp-option-meta">{card.scheduleLabel}</div>}
      </div>
      <div className="tbp-option-card-right">
        {!hidePrices && !!card.priceLabel && <PdfPrice label={card.priceLabel} className="tbp-option-price" />}
      </div>
    </div>
  );
};

const PdfDestinationImage: FC<{ imageUrl?: string }> = ({ imageUrl }) => {
  const blobUrl = getBlobUrl();
  const [failed, setFailed] = useState(false);

  if (!imageUrl || failed) {
    return null;
  }

  return (
    <img
      className="tbp-destination-img"
      src={resolveGalleryImageUrl(blobUrl, imageUrl)}
      alt=""
      onError={() => setFailed(true)}
    />
  );
};

const PdfDestinationHeader: FC<{
  city: string;
  dateLabel?: string;
  nights?: number | null;
  destinationIndex: number;
  imageUrl?: string;
}> = ({ city, dateLabel, nights, destinationIndex, imageUrl }) => {
  const { t } = useTranslation(undefined, { i18n: i18nTB });

  return (
    <div className="tbp-destination">
      <div className="tbp-destination-left">
        <PdfDestinationImage imageUrl={imageUrl} />
        <div className="tbp-destination-text">
          <div className="tbp-destination-city">
            <span className="tbp-destination-index">{destinationIndex}</span>
            {city}
          </div>
          {(!!dateLabel || nights != null) && (
            <div className="tbp-destination-meta">
              {dateLabel}
              {nights != null && (
                <>
                  {!!dateLabel && ' '}
                  ({nights} {nights === 1 ? t('night') : t('nights')})
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const collectBeforeFlights = (slots: TripPreviewSlotView[]) => {
  const flights: Array<{ card: TripPreviewFlightCard; title?: string; key: string }> = [];
  slots.forEach((slot) => {
    if (!slot.beforeSection) {
      return;
    }
    const flight = slot.beforeSection.flights.find((card) => card.isActive);
    if (flight) {
      flights.push({
        card: flight,
        title: slot.beforeSection.sectionTitle,
        key: `before-${slot.rootSlotKey}-${flight.key}`,
      });
    }
  });
  return flights;
};

const buildDestinationIndexBySlot = (slots: TripPreviewSlotView[]) => {
  const map: Record<string, number> = {};
  let index = 0;
  slots.forEach((slot) => {
    slot.chainNodes.forEach((node) => {
      const activeHotel = node.hotelSection?.hotels.find((card) => card.isActive);
      if (activeHotel) {
        index += 1;
        map[node.slotKey] = index;
      }
    });
  });
  return map;
};

const buildDestinationSlotKeysInOrder = (slots: TripPreviewSlotView[]) => {
  const keys: string[] = [];
  slots.forEach((slot) => {
    slot.chainNodes.forEach((node) => {
      const activeHotel = node.hotelSection?.hotels.find((card) => card.isActive);
      const activeTransfer = node.transferSection?.transfers.find((card) => card.isActive);
      if (activeHotel || activeTransfer) {
        keys.push(node.slotKey);
      }
    });
  });
  return keys;
};

const TripBuilderGetPdfContent: FC<TripBuilderGetPdfContentProps> = ({
  model,
  proposal,
  selectedRoomByHotel,
  hidePrices,
  hotelImageBySlot,
  uiVersion = 1,
}) => {
  const { t, i18n } = useTranslation(undefined, { i18n: i18nTB });
  const isRtl = i18n.language === 'he';

  const beforeFlights = useMemo(() => collectBeforeFlights(model.slots), [model.slots]);

  const destinationIndexBySlot = useMemo(
    () => buildDestinationIndexBySlot(model.slots),
    [model.slots],
  );

  const destinationSlotKeysInOrder = useMemo(
    () => buildDestinationSlotKeysInOrder(model.slots),
    [model.slots],
  );

  return (
    <div
      className={'tbp-root' + (uiVersion === 2 ? ' tbp-root-v2' : '') + (isRtl ? ' tbp-root-rtl' : '')}
    >
      {!!beforeFlights.length && (
        <div className="tbp-section">
          <div className="tbp-section-label">{t('flights')}</div>
          <div className="tbp-card-list">
            {beforeFlights.map(({ card, title, key }) => (
              <Fragment key={key}>
                {!!title && <div className="tbp-flight-row-title">{title}</div>}
                <PdfFlightCard card={card} hidePrices={hidePrices} />
              </Fragment>
            ))}
          </div>
        </div>
      )}

      <div className="tbp-section">
        <div className="tbp-section-label">{t('itinerary')}</div>

        {model.slots.map((slot) => (
          <Fragment key={slot.rootSlotKey}>
            {slot.chainNodes.map((node, nodeIndex) => {
              const activeHotel = node.hotelSection?.hotels.find((card) => card.isActive);
              const activeTransfer = node.transferSection?.transfers.find((card) => card.isActive);
              const slotImageUrl = hotelImageBySlot?.[node.slotKey];
              const destinationSlotIndex = destinationSlotKeysInOrder.indexOf(node.slotKey);
              const hasNextDestination =
                destinationSlotIndex !== -1 &&
                destinationSlotIndex < destinationSlotKeysInOrder.length - 1;

              return (
                <Fragment key={`${slot.rootSlotKey}-node-${nodeIndex}`}>
                  {(activeHotel || activeTransfer) && (
                    <>
                      <div className="tbp-destination-block">
                        {activeHotel && (
                          <>
                            <PdfDestinationHeader
                              city={node.city}
                              dateLabel={node.dateLabel}
                              nights={activeHotel.nights}
                              destinationIndex={destinationIndexBySlot[node.slotKey] ?? 0}
                              imageUrl={slotImageUrl}
                            />
                            <PdfHotelCard
                              card={activeHotel}
                              selectedRoomIndex={selectedRoomByHotel[activeHotel.key] ?? 0}
                              hidePrices={hidePrices}
                            />
                          </>
                        )}
                        {activeHotel && activeTransfer && <div className="tbp-destination-divider" />}
                        {activeTransfer && (
                          <PdfTransferCard card={activeTransfer} hidePrices={hidePrices} />
                        )}
                      </div>
                      {hasNextDestination && (
                        <div className="tbp-destination-separator">
                        </div>
                      )}
                    </>
                  )}

                  {node.middleSections.map((section) => {
                    const flight = section.flights.find((card) => card.isActive);
                    if (!flight) {
                      return null;
                    }
                    return (
                      <div
                        className="tbp-card-list tbp-flight-section"
                        key={section.transitionKey}
                      >
                        {!!section.sectionTitle && (
                          <div className="tbp-flight-row-title">{section.sectionTitle}</div>
                        )}
                        <PdfFlightCard card={flight} hidePrices={hidePrices} />
                      </div>
                    );
                  })}
                </Fragment>
              );
            })}

            {slot.afterSection && (() => {
              const flight = slot.afterSection.flights.find((card) => card.isActive);
              if (!flight) {
                return null;
              }
              return (
                <div className="tbp-card-list tbp-flight-section">
                  {!!slot.afterSection.sectionTitle && (
                    <div className="tbp-flight-row-title">{slot.afterSection.sectionTitle}</div>
                  )}
                  <PdfFlightCard card={flight} hidePrices={hidePrices} />
                </div>
              );
            })()}
          </Fragment>
        ))}
      </div>

      <TripBuilderGetPdfRoute slots={model.slots} proposal={proposal} />

      <div className="tbp-total">
        <span className="tbp-total-label">{t('grand_total')}</span>
        <PdfPrice label={model.totalLabel} className="tbp-total-amount" />
      </div>
    </div>
  );
};

export default TripBuilderGetPdfContent;
